/**
 * i18n setup (i18next + react-i18next). All user-visible strings go through
 * `t('...')`; keys are type-checked against `locales/ru.json` (source of truth).
 * Every key must exist in every locale (enforced by `i18n.test.ts`).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ru from './locales/ru.json';

export const SUPPORTED_LOCALES = ['ru', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'ru';

export const resources = {
  ru: { translation: ru },
  en: { translation: en },
} as const;

const STORAGE_KEY = 'zadok.locale';

export function isLocale(value: unknown): value is Locale {
  return SUPPORTED_LOCALES.includes(value as Locale);
}

/**
 * Anonymous visitors: saved choice, then browser language, then default.
 * Phase 2: an authenticated user's profile locale takes precedence.
 */
export function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Storage may be unavailable (privacy mode); fall through.
  }
  const browser = navigator.language.slice(0, 2).toLowerCase();
  return isLocale(browser) ? browser : DEFAULT_LOCALE;
}

export async function changeLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not critical: the choice just won't persist.
  }
  await i18n.changeLanguage(locale);
}

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  interpolation: { escapeValue: false }, // React escapes output
  returnNull: false,
});

export { i18n };
